import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useLivePoll } from "../lib/live";
import { Bell, Package, Truck, CheckCircle2, RotateCcw, Clock, Activity, AlertTriangle, AlertOctagon, TrendingUp, ArrowUpRight, CalendarDays, MapPin, Filter, Tag, ChevronDown, } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";

const KpiCard = ({ icon: Icon, label, value, accent, testid, delta }) => (
  <div className="card-soft p-5 relative overflow-hidden group" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-2xl bg-[var(--brand-soft)] flex items-center justify-center text-[var(--brand)]">
        <Icon size={18} strokeWidth={2} />
      </div>
      {delta != null && (
        <span className={`pill ${delta >= 0 ? "pill-success" : "pill-danger"}`}>
          <ArrowUpRight size={11} /> {delta >= 0 ? "+" : ""}{delta}%
        </span>
      )}
    </div>
    <div className="text-xs text-[var(--text-2)] font-medium">{label}</div>
    <div className="font-display text-3xl font-bold text-[var(--text-1)] mt-1">{value}</div>
    {accent && <div className="mt-1 text-xs text-[var(--text-2)]">{accent}</div>}
  </div>
);

const ALERT_ICONS = { warning: AlertTriangle, danger: AlertOctagon, info: Bell };
const DAILY_SERIES = [
  { key: "received", labelKey: "chart_daily_received", color: "#94A3B8" },
  { key: "in_route", labelKey: "chart_daily_in_route", color: "#2C48E0" },
  { key: "attempted", labelKey: "chart_daily_attempted", color: "#F59E0B" },
  { key: "delivered", labelKey: "chart_daily_delivered", color: "#10B981" },
  { key: "canceled", labelKey: "chart_daily_canceled", color: "#64748B" },
  { key: "returned", labelKey: "chart_daily_returned", color: "#EF4444" },
];
const PERIOD_OPTIONS = [
  { key: "7d", labelKey: "chart_period_7d" },
  { key: "30d", labelKey: "chart_period_30d" },
  { key: "month", labelKey: "chart_period_month" },
  { key: "semester", labelKey: "chart_period_semester" },
  { key: "year", labelKey: "chart_period_year" },
  { key: "custom", labelKey: "chart_period_custom" },
];
const STATUS_OPTIONS = [
  { key: "received", labelKey: "status_received" },
  { key: "out_for_delivery", labelKey: "status_out_for_delivery" },
  { key: "attempted", labelKey: "status_attempted" },
  { key: "delivered", labelKey: "status_delivered" },
  { key: "returned", labelKey: "status_returned" },
  { key: "canceled", labelKey: "chart_daily_canceled" },
];
const STATUS_SERIES = {
  received: "received",
  out_for_delivery: "in_route",
  attempted: "attempted",
  delivered: "delivered",
  canceled: "canceled",
  returned: "returned",
};

function localYmd(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function defaultCustomRange() {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 29);
  return { from: localYmd(from), to: localYmd(to) };
}

function tooltipStyle() {
  return { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 };
}

function hasSeries(rows, keys) {
  return (rows || []).some((row) => keys.some((key) => Number(row[key] || 0) > 0));
}

function StatusCombobox({ value, onChange, t }) {
  const selected = STATUS_OPTIONS.filter((option) => value.includes(option.key));
  const label = selected.length === 0
    ? t("chart_all_statuses")
    : selected.map((option) => t(option.labelKey)).join(", ");

  const toggle = (key) => {
    onChange(value.includes(key) ? value.filter((item) => item !== key) : [...value, key]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full min-w-[220px] items-center justify-between rounded-xl border border-[var(--border-c)] px-3 text-xs font-semibold text-[var(--text-1)] shadow-sm hover:bg-[var(--surface-2)]"
          data-testid="chart-status-filter"
        >
          <span className="truncate text-left">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[240px] p-1 rounded-xl">
        <div className="max-h-64 overflow-y-auto">
          {STATUS_OPTIONS.map((option) => {
            const checked = value.includes(option.key);
            return (
              <label
                key={option.key}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-[var(--text-1)] hover:bg-[var(--surface-2)] cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(option.key)}
                  data-testid={`chart-status-${option.key}`}
                />
                <span>{t(option.labelKey)}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [live, setLive] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState([]);
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [chartsLoading, setChartsLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    const params = new URLSearchParams({ period });
    if (region) params.set("region", region);
    if (status.length) params.set("status", status.join(","));
    if (period === "custom") {
      params.set("from", customRange.from);
      params.set("to", customRange.to);
    }
    setChartsLoading(true);
    try {
      const [{ data: s }, { data: a }] = await Promise.all([
        api.get(`/dashboard/summary?${params.toString()}`),
        api.get("/dashboard/alerts"),
      ]);
      setSummary(s);
      setAlerts(a);
    } finally {
      setChartsLoading(false);
    }
  }, [period, region, status, customRange]);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/live");
      setLive(data);
    } catch {
      
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useLivePoll(loadLive, 15000);

  const handlePeriod = (next) => setPeriod(next);

  const handleRegion = (next) => setRegion(next === "__all__" ? "" : next);

  const handleCustomRange = (nextRange) => setCustomRange(nextRange);

  if (!summary) return <div className="text-[var(--text-2)] p-6">Carregando...</div>;
  const k = summary.kpis;
  const filters = summary.filters || {};
  const regions = summary.regions || [];
  const rangeLabel = filters.range_label;
  const statusLabels = STATUS_OPTIONS.filter((option) => status.includes(option.key)).map((option) => t(option.labelKey));
  const statusLabel = statusLabels.join(", ");
  const chartScope = [rangeLabel, region, statusLabel].filter(Boolean).join(" · ");
  const regionChartTitle = filters.grouped_by === "neighborhood" ? t("chart_region_neighborhood") : t("chart_region");
  const visibleSeries = status.length
    ? DAILY_SERIES.filter((series) => status.some((item) => STATUS_SERIES[item] === series.key))
    : DAILY_SERIES;
  const dailyHasData = hasSeries(summary.daily_deliveries, visibleSeries.map((s) => s.key));
  const regionHasData = hasSeries(summary.by_region, ["count"]);

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">Painel executivo · Tempo real</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Bem-vindo, {user?.name?.split(" ")[0]}</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="pill pill-success"><span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] pulse-dot" /> {live ? "Live" : "Online"}</span>
          {live?.kpis && (
            <span className="pill pill-info">
              {live.kpis.in_route} em rota
              {user?.role !== "deliverer" ? ` · ${live.kpis.pending_imports} imports pendentes` : ""}
            </span>
          )}
          <span className="pill pill-info">{k.total} documentos</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Package} label={t("kpi_received_today")} value={k.received_today} testid="kpi-received-today" />
        <KpiCard icon={Activity} label={t("kpi_in_processing")} value={k.in_processing} testid="kpi-in-processing" />
        <KpiCard icon={Truck} label={t("kpi_in_route")} value={k.in_route} testid="kpi-in-route" />
        <KpiCard icon={CheckCircle2} label={t("kpi_delivered")} value={k.delivered} testid="kpi-delivered" />
        <KpiCard icon={RotateCcw} label={t("kpi_returned")} value={k.returned} testid="kpi-returned" />
        <KpiCard icon={Clock} label={t("kpi_pending")} value={k.pending} testid="kpi-pending" />
        <KpiCard icon={TrendingUp} label={t("kpi_success_rate")} value={`${k.success_rate}%`} testid="kpi-success-rate" />
        <KpiCard icon={Package} label={t("kpi_total")} value={k.total} testid="kpi-total" />
      </div>

      <div className="card-soft p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
              <Filter size={15} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[var(--text-1)]">{t("chart_filters")}</div>
              <div className="text-[10px] text-[var(--text-2)]">{t("chart_filters_hint")}</div>
            </div>
          </div>
          {rangeLabel && <span className="pill pill-info font-mono text-[11px]">{rangeLabel}</span>}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="card-soft p-1 flex items-center gap-1 overflow-x-auto">
            {PERIOD_OPTIONS.map(({ key, labelKey }) => (
              <button
                key={key}
                type="button"
                onClick={() => handlePeriod(key)}
                className={`h-9 px-3 rounded-2xl text-xs font-semibold whitespace-nowrap transition-colors ${
                  period === key
                    ? "bg-[var(--brand)] text-white shadow-sm"
                    : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                }`}
                data-testid={`chart-period-${key}`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 min-w-[220px]">
            <MapPin size={14} className="text-[var(--text-3)] shrink-0" />
            <Select value={region || "__all__"} onValueChange={handleRegion}>
              <SelectTrigger className="rounded-xl h-9 border-[var(--border-c)] text-xs font-semibold" data-testid="chart-region-filter">
                <SelectValue placeholder={t("chart_all_regions")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("chart_all_regions")}</SelectItem>
                {regions.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 min-w-[220px]">
            <Tag size={14} className="text-[var(--text-3)] shrink-0" />
            <StatusCombobox value={status} onChange={setStatus} t={t} />
          </div>
        </div>

        {period === "custom" && (
          <div className="rounded-2xl bg-[var(--surface-2)] border border-[var(--border-c)] px-4 py-3 mt-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-2)] shrink-0">
              <div className="w-8 h-8 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
                <CalendarDays size={15} />
              </div>
              {t("chart_custom_range")}
            </div>
            <label className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-3)]">
              {t("chart_from")}
              <Input
                type="date"
                value={customRange.from}
                onChange={(e) => handleCustomRange({ ...customRange, from: e.target.value })}
                className="rounded-xl h-9 w-[150px] mt-1"
                data-testid="chart-date-from"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-3)]">
              {t("chart_to")}
              <Input
                type="date"
                value={customRange.to}
                onChange={(e) => handleCustomRange({ ...customRange, to: e.target.value })}
                className="rounded-xl h-9 w-[150px] mt-1"
                data-testid="chart-date-to"
              />
            </label>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 ${chartsLoading ? "opacity-70" : ""}`}>
        <div className="lg:col-span-2 card-soft p-5">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-1)]">{t("chart_daily")}</h2>
              {chartScope && <div className="text-[10px] text-[var(--text-2)] mt-0.5">{chartScope}</div>}
            </div>
          </div>
          {!dailyHasData ? (
            <div className="text-sm text-[var(--text-2)] py-16 text-center">{t("no_data")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={summary.daily_deliveries}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} interval="preserveStartEnd" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle()} />
                <Legend />
                {visibleSeries.map((series) => (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={t(series.labelKey)}
                    stroke={series.color}
                    strokeWidth={series.key === "delivered" ? 2.5 : 2}
                    dot={{ fill: series.color, r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card-soft p-5">
          <h2 className="text-sm font-semibold text-[var(--text-1)] mb-3">{t("alerts")}</h2>
          <div className="space-y-2">
            {alerts.length === 0 && <div className="text-sm text-[var(--text-2)]">{t("no_alerts")}</div>}
            {alerts.map((a) => {
              const Icon = ALERT_ICONS[a.level] || Bell;
              return (
                <div key={a.id} className="rounded-2xl p-3 border border-[var(--border-c)] flex items-start gap-3" data-testid={`alert-${a.id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.level === "danger" ? "bg-[var(--danger-soft)] text-[var(--danger)]" : "bg-[var(--warning-soft)] text-[var(--warning-text)]"}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center justify-between gap-2">
                      <span className="truncate">{a.title}</span>
                      <span className="pill pill-neutral font-mono">{a.count}</span>
                    </div>
                    <div className="text-xs text-[var(--text-2)] mt-0.5">{a.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={`card-soft p-5 ${chartsLoading ? "opacity-70" : ""}`}>
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-[var(--text-1)]">{regionChartTitle}</h2>
          {chartScope && <div className="text-[10px] text-[var(--text-2)] mt-0.5">{chartScope}</div>}
        </div>
        {!regionHasData ? (
          <div className="text-sm text-[var(--text-2)] py-16 text-center">{t("no_data")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={summary.by_region} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="region" stroke="hsl(var(--muted-foreground))" fontSize={11} width={110} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Bar dataKey="count" name={statusLabel || t("chart_daily_delivered")} fill="#43E695" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
